import {
  createSystem,
  defaultBaseConfig,
  defaultConfig,
  defineConfig,
  mergeConfigs,
} from '@chakra-ui/react';
const systemFont = [
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Barlow',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
];
const semanticTokens = {
  bg: {
    t0: {
      _light: 'white',
      _dark: '#000',
    },
    t1: {
      _light: '#F8F8F9',
      _dark: '#18191D',
    },
    t2: {
      _light: '#F4F5F5',
      _dark: '#1D1F26',
    },
    card1: {
      _light: '#171a1c',
      _dark: '#171a1c',
    },
    card2: {
      _light: '#2b3246',
      _dark: '#2b3246',
    },
    card3: {
      _light: '#1E2328',
      _dark: '#1E2328',
    },
    card4: {
      _light: '#f7f7f8',
      _dark: '#f7f7f8'
    }
  },
  text: {
    t1: {
      _light: '#06080B',
      _dark: 'white',
    },
    t2: {
      _light: 'rgba(6, 8, 11, 0.60)',
      _dark: 'rgba(255, 255, 255, 0.60)',
    },
    t3: {
      _light: 'rgba(6, 8, 11, 0.40)',
      _dark: 'rgba(255, 255, 255, 0.40)',
    },
    t4: {
      _light: 'rgba(6, 8, 11, 0.05)',
      _dark: 'rgba(255, 255, 255, 0.10)',
    },
    primary: {
      _light: 'rgba(101, 167, 22, 1)',
      _dark: 'rgba(171, 234, 69, 1)',
    },
    expand: {
      _light: '#047AFF',
      _dark: '#047AFF'
    },
    footnote: {
      _light: '#072723',
      _dark: '#072723'
    }
  },
  profit: {
    _light: '#179b90',
    _dark: '#179b90',
  },
  loss: {
    _light: '#e34376',
    _dark: '#e34376',
  },
  border: {
    _light: '#E7EAEB',
    _dark: '#E7EAEB'
  }
};
const customConfig = defineConfig({
  strictTokens: false,
  theme: {
    breakpoints: {
      // for download page
      dlg: '768px',
      dxl: '1440px',
      '2dxl': '1920px',
    },
    recipes: {
      button: {
        variants: {
          variant: {
            primary: {
              color: 'text.t2',
              bg: 'bg.t0',
            },
          },
        },
      },
      icon: {
        base: {
          color: 'text.t1',
        },
      },
      input: {
        base: {
          color: 'text.t1',
          borderColor: 'text.t4',
        },
      },
      text: {
        base: {
          color: 'text.t1',
        },
      },
    },
    tokens: {
      fonts: {
        body: { value: 'var(--font-poppins), sans-serif' },
      },
      colors: {
        fixed: {
          warning: { value: '#FEA75B' },
          primary: { value: '#ABEA45' },
          green: { value: '#01BC8D' },
          long: { value: '#6FEBB9' },
          longHover: { value: 'rgba(111, 235, 185, 0.1)' },
          short: { value: ' #FFA79B' },
          shortHover: { value: 'rgba(241, 81, 105, 0.1)' },
          white: { value: '#ffffff' },
          'quant-dark-bg': { value: '#0A0B14' },
        },
      },
    },
    semanticTokens: {
      colors: {
        bg: {
          t0: {
            value: {
              _light: semanticTokens.bg.t0._light,
              _dark: semanticTokens.bg.t0._dark,
            },
          },

          t1: {
            value: {
              _light: semanticTokens.bg.t1._light,
              _dark: semanticTokens.bg.t1._dark,
            },
          },
          t2: {
            value: {
              _light: semanticTokens.bg.t2._light,
              _dark: semanticTokens.bg.t2._dark,
            },
          },
          card1: {
            value: {
              _light: semanticTokens.bg.card1._light,
              _dark: semanticTokens.bg.card1._dark,
            }
          },
          card2: {
            value: {
              _light: semanticTokens.bg.card2._light,
              _dark: semanticTokens.bg.card2._dark,
            }
          },
          card3: {
            value: {
              _light: semanticTokens.bg.card3._light,
              _dark: semanticTokens.bg.card3._dark,
            }
          },
          card4: {
            value: {
              _light: semanticTokens.bg.card4._light,
              _dark: semanticTokens.bg.card4._dark,
            }
          }
        },
        'bg-inverted': {
          t0: {
            value: {
              _light: semanticTokens.bg.t0._dark,
              _dark: semanticTokens.bg.t0._light,
            },
          },
          t1: {
            value: {
              _light: semanticTokens.bg.t1._dark,
              _dark: semanticTokens.bg.t1._light,
            },
          },
          t2: {
            value: {
              _light: semanticTokens.bg.t2._dark,
              _dark: semanticTokens.bg.t2._light,
            },
          },
        },
        text: {
          t1: {
            value: {
              _light: semanticTokens.text.t1._light,
              _dark: semanticTokens.text.t1._dark,
            },
          },
          t2: {
            value: {
              _light: semanticTokens.text.t2._light,
              _dark: semanticTokens.text.t2._dark,
            },
          },
          t3: {
            value: {
              _light: semanticTokens.text.t3._light,
              _dark: semanticTokens.text.t3._dark,
            },
          },
          t4: {
            value: {
              _light: semanticTokens.text.t4._light,
              _dark: semanticTokens.text.t4._dark,
            },
          },
          primary: {
            value: {
              _light: semanticTokens.text.primary._light,
              _dark: semanticTokens.text.primary._dark,
            },
          },
          expand: {
            value: {
              _light: semanticTokens.text.expand._light,
              _dark: semanticTokens.text.expand._dark,
            },
          },
          footnote: {
            value: {
              _light: semanticTokens.text.footnote._light,
              _dark: semanticTokens.text.footnote._dark,
            },
          }
        },
        'text-inverted': {
          t1: {
            value: {
              _light: semanticTokens.text.t1._dark,
              _dark: semanticTokens.text.t1._light,
            },
          },
          t2: {
            value: {
              _light: semanticTokens.text.t2._dark,
              _dark: semanticTokens.text.t2._light,
            },
          },
          t3: {
            value: {
              _light: semanticTokens.text.t3._dark,
              _dark: semanticTokens.text.t3._light,
            },
          },
          t4: {
            value: {
              _light: semanticTokens.text.t4._dark,
              _dark: semanticTokens.text.t4._light,
            },
          },
        },
        profit: {
          value: {
            _light: semanticTokens.profit._light,
            _dark: semanticTokens.profit._dark,
          },
        },
        loss: {
          value: {
            _light: semanticTokens.loss._light,
            _dark: semanticTokens.loss._dark,
          },
        },
        border: {
          value: {
            _light: semanticTokens.border._light,
            _dark: semanticTokens.border._dark,
          },
        }
      },
    },
  },
});
export const system = createSystem(
  defaultBaseConfig,
  mergeConfigs(customConfig, defaultConfig),
);
